import React from 'react';
import { motion } from 'framer-motion';
interface PaymentIconsProps {
  className?: string;
}
/**
 * Unified payment trust block: institutional full-color payment logos in a single row,
 * followed by a discreet trust legend. Placed directly below the trust line of every CTA.
 */
export function PaymentIcons({
  className = ''
}: PaymentIconsProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.4,
    delay: 0.1
  }} className={`mt-5 flex flex-col items-center gap-2.5 ${className}`} aria-label="Métodos de pago aceptados" data-id="element-135">
      <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-2.5" data-id="element-136">
        <VisaLogo data-id="element-137" />
        <MastercardLogo data-id="element-138" />
        <PaypalLogo data-id="element-139" />
        <AmexLogo data-id="element-140" />
        <ApplePayLogo data-id="element-141" />
        <GooglePayLogo data-id="element-142" />
        <ShopifySecureBadge data-id="element-143" />
      </div>
      <p className="text-[11px] sm:text-xs text-brand-muted/80 italic font-medium tracking-wide text-center max-w-md px-2" data-id="element-144">
        Pago único. Sin suscripciones ocultas. Entrega digital inmediata vía
        Email.
      </p>
    </motion.div>;
}
/* ------------------------------------------------------------------ */
/*  Uniform badge wrapper — same height, padding & shadow across all  */
/* ------------------------------------------------------------------ */
const BADGE_HEIGHT = 'h-9 sm:h-10';
const BADGE_PADDING = 'px-2.5 sm:px-3';
interface BadgeProps {
  children: React.ReactNode;
  bg?: string;
  label: string;
}
function Badge({
  children,
  bg = 'bg-white',
  label
}: BadgeProps) {
  return <div role="img" aria-label={label} className={`flex items-center justify-center ${BADGE_HEIGHT} ${BADGE_PADDING} rounded-md ${bg} border border-black/5 shadow-[0_1px_2px_rgba(0,0,0,0.08)]`} data-id="element-145">
      {children}
    </div>;
}
/* --------------------------- Logo glyphs --------------------------- */
function VisaLogo() {
  return <Badge label="Visa" data-id="element-146">
      <svg viewBox="0 0 60 18" className="h-3.5 sm:h-4 w-auto" xmlns="http://www.w3.org/2000/svg" data-id="element-147">
        <text x="30" y="15" textAnchor="middle" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fontStyle="italic" fontSize="18" fill="#1A1F71" letterSpacing="0" data-id="element-148">
          VISA
        </text>
      </svg>
    </Badge>;
}
function MastercardLogo() {
  return <Badge label="Mastercard" data-id="element-149">
      <svg viewBox="0 0 40 24" className="h-5 sm:h-6 w-auto" xmlns="http://www.w3.org/2000/svg" data-id="element-150">
        <circle cx="15" cy="12" r="9" fill="#EB001B" data-id="element-151" />
        <circle cx="25" cy="12" r="9" fill="#F79E1B" data-id="element-152" />
        {/* Intersection */}
        <path d="M20 5.4a9 9 0 0 1 0 13.2 9 9 0 0 1 0-13.2z" fill="#FF5F00" data-id="element-153" />
      </svg>
    </Badge>;
}
function PaypalLogo() {
  return <Badge label="PayPal" data-id="element-154">
      <svg viewBox="0 0 80 22" className="h-3.5 sm:h-4 w-auto" xmlns="http://www.w3.org/2000/svg" data-id="element-155">
        {/* Stylized PayPal wordmark with double-P shadow effect */}
        <g data-id="element-156">
          {/* Back "Pay" - light blue */}
          <text x="3" y="18" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fontStyle="italic" fontSize="20" fill="#009CDE" data-id="element-157">
            PayPal
          </text>
          {/* Front "Pay" - dark blue */}
          <text x="0" y="16" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fontStyle="italic" fontSize="20" fill="#003087" data-id="element-158">
            Pay
          </text>
        </g>
      </svg>
    </Badge>;
}
function AmexLogo() {
  return <Badge bg="bg-[#2E77BB]" label="American Express" data-id="element-159">
      <svg viewBox="0 0 50 24" className="h-5 sm:h-6 w-auto" xmlns="http://www.w3.org/2000/svg" data-id="element-160">
        <text x="25" y="11" textAnchor="middle" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fontSize="7" fill="#FFFFFF" letterSpacing="0.5" data-id="element-161">
          AMERICAN
        </text>
        <text x="25" y="19" textAnchor="middle" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fontSize="7" fill="#FFFFFF" letterSpacing="0.5" data-id="element-162">
          EXPRESS
        </text>
      </svg>
    </Badge>;
}
function ApplePayLogo() {
  return <Badge bg="bg-black" label="Apple Pay" data-id="element-163">
      <svg viewBox="0 0 44 18" className="h-3.5 sm:h-4 w-auto" xmlns="http://www.w3.org/2000/svg" fill="#FFFFFF" data-id="element-164">
        {/* Apple logo */}
        <path d="M9.5 4.7c.6-.7.9-1.6.8-2.6-.8 0-1.7.5-2.3 1.2-.5.6-.9 1.5-.8 2.4.9.1 1.7-.4 2.3-1zm.8.9c-1.2-.1-2.3.7-2.9.7-.6 0-1.5-.7-2.5-.7-1.3 0-2.5.8-3.2 1.9C.4 9.9 1.3 13.3 2.7 15.2c.7 1 1.5 2 2.4 2 .9 0 1.3-.6 2.5-.6s1.5.6 2.5.6c1 0 1.7-1 2.3-2 .8-1.1 1.1-2.1 1.1-2.2 0 0-2.1-.8-2.1-3.2 0-2 1.6-2.9 1.7-3-.9-1.4-2.3-1.3-2.8-1.4z" data-id="element-165" />
        {/* Pay text */}
        <text x="16" y="13" fontFamily="-apple-system, Helvetica, Arial, sans-serif" fontWeight="600" fontSize="11" fill="#FFFFFF" data-id="element-166">
          Pay
        </text>
      </svg>
    </Badge>;
}
function GooglePayLogo() {
  return <Badge label="Google Pay" data-id="element-167">
      <svg viewBox="0 0 70 18" className="h-3.5 sm:h-4 w-auto" xmlns="http://www.w3.org/2000/svg" data-id="element-168">
        {/* Google "G" multicolor circle */}
        <g transform="translate(0, 2)" data-id="element-169">
          <path d="M14 7c0-.5-.04-1-.13-1.4H7v2.8h3.93c-.17.9-.68 1.66-1.45 2.17v1.8h2.34C13.18 11.1 14 9.24 14 7z" fill="#4285F4" data-id="element-170" />
          <path d="M7 14c1.95 0 3.58-.65 4.78-1.76l-2.34-1.8c-.65.43-1.48.69-2.44.69-1.88 0-3.47-1.27-4.04-2.97H.54v1.86A7 7 0 0 0 7 14z" fill="#34A853" data-id="element-171" />
          <path d="M2.96 8.16A4.2 4.2 0 0 1 2.74 7c0-.4.07-.79.18-1.16V3.98H.54A6.97 6.97 0 0 0 0 7c0 1.13.27 2.2.74 3.16l2.22-1.72z" fill="#FBBC04" data-id="element-172" />
          <path d="M7 2.84c1.06 0 2 .36 2.75 1.07l2.06-2.06C10.57.71 8.95 0 7 0A7 7 0 0 0 .54 3.98L2.96 5.84C3.53 4.14 5.12 2.84 7 2.84z" fill="#EA4335" data-id="element-173" />
        </g>
        {/* Pay text */}
        <text x="18" y="13" fontFamily="Arial, sans-serif" fontWeight="500" fontSize="11" fill="#5F6368" data-id="element-174">
          Pay
        </text>
      </svg>
    </Badge>;
}
function ShopifySecureBadge() {
  return <div role="img" aria-label="Shopify Secure Checkout" className={`flex items-center gap-1.5 ${BADGE_HEIGHT} ${BADGE_PADDING} rounded-md bg-[#5E8E3E] border border-[#4C7A30] shadow-[0_1px_2px_rgba(0,0,0,0.08)]`} data-id="element-175">
      {/* Shopify bag glyph */}
      <svg viewBox="0 0 20 22" className="h-4 sm:h-4.5 w-auto" xmlns="http://www.w3.org/2000/svg" fill="#FFFFFF" data-id="element-176">
        <path d="M14.7 4.3c0-.1 0-.2-.1-.2L13.4.2c-.1-.2-.2-.3-.4-.2L11.6.4C11 .2 10.3 0 9.6 0 6.7 0 5.2 1.9 4.7 3.8c-.6.2-1.3.4-2 .6-.6.2-.6.2-.7.8C1.9 5.7 0 19.1 0 19.1L13 21l5.6-1.2C18.7 19.7 14.7 4.4 14.7 4.3zM10.6 1.6l-1 .3c0-.6-.1-1.3-.4-1.9.9.2 1.3 1.1 1.4 1.6zM9 2.1l-2.4.7c.3-1 .8-1.9 1.9-2 .2.4.4 1 .5 1.3zm-1.7-2c-1.5.1-2.4 1.5-2.8 3.1l-1.6.5C3.4 2.2 4.7.4 7.3.1z" data-id="element-177" />
      </svg>
      <div className="flex flex-col leading-none" data-id="element-178">
        <span className="text-[10px] sm:text-[11px] font-bold text-white tracking-tight" data-id="element-179">
          Shopify
        </span>
        <span className="text-[8px] sm:text-[9px] font-semibold text-white/90 tracking-wider mt-0.5" data-id="element-180">
          SECURE
        </span>
      </div>
    </div>;
}